'use client'

import { useMemo } from 'react'

import { useInfiniteQuery, useQuery } from '@tanstack/react-query'

import type { ActivityModel } from '@/lib/api/models'
import { getActivityDetail, getActivityList } from '@/lib/api/services'

import { ACTIVITY_LIST_KEY } from '../explore/explore-prefetch'

// 重新导出 query key 以保持向后兼容
export { ACTIVITY_LIST_KEY } from '../explore/explore-prefetch'
export const ACTIVITY_DETAIL_KEY = 'ACTIVITY_DETAIL_KEY' as const

export const useActivityList = (pageSize = 20, enabled = true) => {
  const query = useInfiniteQuery({
    queryKey: [ACTIVITY_LIST_KEY, pageSize],
    queryFn: async ({ pageParam = 0 }) => {
      const res = await getActivityList({
        page: pageParam,
        pageSize
      })
      return res
    },
    getNextPageParam: (lastPage, allPages, lastPageParam) => {
      if (lastPage.list.length === 0) {
        return undefined
      }
      return lastPageParam + 1
    },
    initialPageParam: 0,
    enabled
  })

  const list = useMemo(() => {
    if (!query.data) return []
    return query.data.pages.reduce((acc: ActivityModel[], page) => {
      return [...acc, ...page.list]
    }, [])
  }, [query.data])

  const total = useMemo(() => {
    if (!query.data?.pages.length) return 0
    return query.data.pages[0].total
  }, [query.data])

  const hasMore = useMemo(() => {
    return query.hasNextPage && !query.isFetchingNextPage
  }, [query.hasNextPage, query.isFetchingNextPage])

  const loadMore = async () => {
    if (hasMore) {
      await query.fetchNextPage()
    }
  }

  return {
    list,
    total,
    hasMore,
    loadMore,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
    isFetchingNextPage: query.isFetchingNextPage
  }
}

export const useActivityDetail = (id: number) => {
  const query = useQuery({
    queryKey: [ACTIVITY_DETAIL_KEY, id],
    enabled: !!id,
    queryFn: async () => {
      const res = await getActivityDetail(id)
      return res
    }
  })

  return query
}
